import React, { useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

import { collapseTransition, groupAnimation } from '@pkg/components/Navigation/SidebarNav/animations';

import { type Competition } from '@app/client/common/structs';
import { FALLBACK_HEADER_IMG_SRC, getMashedUrl } from '@app/helpers';
import { useFantasyTracking } from '@app/pages/FantasyWishlist/hooks';
import { TimeLeft } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/BidCard/components';
import { useNavigationStore } from '@app/store/zustand';

import {
    ActionsWrapper,
    ActiveBorder,
    ButtonsWrapper,
    ButtonTextWrapper,
    CollapseableArea,
    CollapseableAreaPadding,
    GameInside,
    GameWrapper,
    HeaderBackground,
    Label,
    TimeWrapper,
    Title,
    TitleWrapper,
} from './styles';

interface GameListItemProps {
    competition: Competition;
    isGameOpen: boolean;
    isSubmitted?: boolean;
    onSetGameOpen?: (open: boolean, id: string) => void;
    canParticipate?: boolean;
}

export const GameListItem = ({ competition, isSubmitted, canParticipate = true }: GameListItemProps) => {
    const isOpen = true;
    const noTransition = true;

    const [submissionsEnd, setSubmissionsEnd] = useState(false);
    const path = `/fantasy/${competition.slug}`;

    const location = useLocation();
    const isActiveRoute = location.pathname.includes(path);
    const toggleMobileMenu = useNavigationStore((state) => state.toggleMobileMenu);
    const { trackNavFantasyClick } = useFantasyTracking();

    const expiration = canParticipate && !isSubmitted ? competition?.submission_ends_at : competition?.ends_at;

    const timeLeftMarkup = useMemo(() => {
        if (!submissionsEnd && expiration) {
            return (
                <TimeWrapper>
                    <TimeLeft compact expiration={expiration} onExpired={() => setSubmissionsEnd(true)} />
                </TimeWrapper>
            );
        }
    }, [expiration, submissionsEnd]);

    const ctaCopy = useMemo(() => {
        if (!canParticipate || submissionsEnd) return 'View game';
        if (isSubmitted) return 'Edit my lineup';
        return 'Create my lineup';
    }, [canParticipate, isSubmitted, submissionsEnd]);

    function handleClick() {
        trackNavFantasyClick(competition.id, competition.slug, 'create-lineup-button');
        toggleMobileMenu(false);
    }

    const createLineupButtonMarkup = (
        <Link to={canParticipate ? `${path}` : `${path}/leaderboard`} onClick={handleClick}>
            <ButtonTextWrapper>
                <div>{ctaCopy}</div>
                {timeLeftMarkup}
            </ButtonTextWrapper>
        </Link>
    );

    const src = competition.header_images?.[0] || competition.sponsor_logo || FALLBACK_HEADER_IMG_SRC;

    const mashedImageUrl = getMashedUrl({
        width: 300,
        url: src,
    });

    return (
        <GameWrapper>
            <GameInside>
                {mashedImageUrl ? <HeaderBackground $image={mashedImageUrl} /> : null}
                <ActionsWrapper>
                    <TitleWrapper>
                        <Label>{competition.game_label}</Label>
                        <Title>{competition.name}</Title>
                    </TitleWrapper>
                </ActionsWrapper>
                <CollapseableArea
                    variants={groupAnimation}
                    animate={isOpen ? 'open' : 'closed'}
                    transition={noTransition ? { duration: 0 } : collapseTransition}
                >
                    <CollapseableAreaPadding>
                        <ButtonsWrapper>{createLineupButtonMarkup}</ButtonsWrapper>
                    </CollapseableAreaPadding>
                </CollapseableArea>
            </GameInside>

            <AnimatePresence initial={false}>
                {isActiveRoute && (
                    <ActiveBorder
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={noTransition ? { duration: 0 } : {}}
                    />
                )}
            </AnimatePresence>
        </GameWrapper>
    );
};
